import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';

interface DateRange {
  from?: string;
  to?: string;
}

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

function toQs(range: DateRange): string {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export interface ReportMetrics {
  totalOrders: number;
  totalValue: number;
  openOrders: number;
  ordersDueThisWeek: number;
}

export interface OrdersByStageRow {
  stageId: string | null;
  stageName: string;
  count: number;
  totalValue: number;
}

export interface OrdersByCompanyRow {
  companyId: string;
  companyName: string;
  count: number;
  totalValue: number;
}

export interface RevenueByMonthRow {
  month: string;
  revenue: number;
  orderCount: number;
}

export function useReportMetrics(range: DateRange) {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<ReportMetrics>({
    queryKey: ['reports', 'metrics', workspaceId, range.from, range.to],
    queryFn: async () => {
      const token = await getToken();
      return api.get<ReportMetrics>(`/v1/reports/metrics${toQs(range)}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useOrdersByStage(range: DateRange) {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<OrdersByStageRow[]>({
    queryKey: ['reports', 'orders-by-stage', workspaceId, range.from, range.to],
    queryFn: async () => {
      const token = await getToken();
      return api.get<OrdersByStageRow[]>(`/v1/reports/orders-by-stage${toQs(range)}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useOrdersByCompany(range: DateRange) {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<OrdersByCompanyRow[]>({
    queryKey: ['reports', 'orders-by-company', workspaceId, range.from, range.to],
    queryFn: async () => {
      const token = await getToken();
      return api.get<OrdersByCompanyRow[]>(`/v1/reports/orders-by-company${toQs(range)}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useRevenueByMonth(range: DateRange) {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<RevenueByMonthRow[]>({
    queryKey: ['reports', 'revenue-by-month', workspaceId, range.from, range.to],
    queryFn: async () => {
      const token = await getToken();
      return api.get<RevenueByMonthRow[]>(`/v1/reports/revenue-by-month${toQs(range)}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}
